import { useEffect, useRef, useState } from 'react'
import { usePerson } from '../contexts/PersonContext'
import { useCreateReport, useAddReportAttachment } from './useSafety'
import { apiError } from '../lib/api/client'
import type { CreateReportRequest, ReportDto } from '../types/community'

/** Preserve the delivered report while retrying only failed evidence. */
export function useReportSubmission() {
  const { activePerson } = usePerson()
  const { mutateAsync: create } = useCreateReport()
  const { mutateAsync: upload } = useAddReportAttachment()
  const [receipt, setReceipt] = useState<ReportDto | null>(null)
  const [failedFiles, setFailedFiles] = useState<File[]>([])
  const [uploadedCount, setUploadedCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saved = useRef<ReportDto | null>(null)
  const locked = useRef(false)
  const generation = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  useEffect(() => { generation.current++; saved.current = null; locked.current = false; setReceipt(null); setFailedFiles([]); setUploadedCount(0); setBusy(false); setError(null) }, [activePerson?.id])

  async function send(request: CreateReportRequest | undefined, files: File[], retry = false) {
    if (locked.current || (!saved.current && !request)) return
    locked.current = true
    const current = generation.current
    const valid = () => mounted.current && generation.current === current
    setBusy(true); setError(null)
    try {
      const report = saved.current ?? await create(request!)
      if (!valid()) return
      saved.current = report
      const failed: File[] = []
      let accepted = 0
      for (const file of files) {
        if (!valid()) return
        try { await upload({ id: report.id, file }); accepted++ } catch { failed.push(file) }
      }
      if (!valid()) return
      setReceipt(report); setFailedFiles(failed)
      setUploadedCount(count => retry ? count + accepted : accepted)
    } catch (err) {
      if (valid()) setError(apiError(err).message)
    } finally {
      if (valid()) { locked.current = false; setBusy(false) }
    }
  }

  function reset() {
    if (locked.current) return
    generation.current++; saved.current = null
    setReceipt(null); setFailedFiles([]); setUploadedCount(0); setError(null)
  }
  return { receipt, failedFiles, uploadedCount, busy, error, send, reset,
    removeFailedFile: (index: number) => { if (!locked.current) setFailedFiles(files => files.filter((_, i) => i !== index)) },
    retryAttachments: () => send(undefined, failedFiles, true) }
}
